import axios from "axios";

export const BASE_URL =
  typeof window !== "undefined" &&
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

// The user's recent search terms, newest first. Kept on the device so the
// feed can keep showing motivation blogs after the search that asked for them
// has been cleared. The backend remembers them per ACCOUNT as well; this copy
// only smooths over the first request after a fresh login.
//
// The key is namespaced by user id: two accounts sharing one browser must not
// inherit each other's searches.
const RECENT_KEY = "feedRecentSearches";
const RECENT_LIMIT = 5;

const recentKey = () => {
  const userId = localStorage.getItem("userId");
  return userId ? `${RECENT_KEY}:${userId}` : RECENT_KEY;
};

function authConfig() {
  const token = localStorage.getItem("token");

  return token
    ? {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    : {};
}

export function getRecentSearches() {
  try {
    const raw = JSON.parse(localStorage.getItem(recentKey()));
    return Array.isArray(raw) ? raw.slice(0, RECENT_LIMIT) : [];
  } catch {
    return [];
  }
}

export function rememberSearch(term) {
  const clean = String(term || "").trim();
  if (clean.length < 2) return getRecentSearches();

  const next = [
    clean,
    ...getRecentSearches().filter(
      (t) => t.toLowerCase() !== clean.toLowerCase()
    ),
  ].slice(0, RECENT_LIMIT);

  try {
    localStorage.setItem(recentKey(), JSON.stringify(next));
  } catch {
    // Private mode / quota — the feed still works, it just forgets.
  }
  return next;
}

// One page of the mixed feed. `signal` comes from an AbortController so a
// stale in-flight request is cancelled the moment the search term moves on.
//
// `seed` is what keeps infinite scroll honest: page 1 is requested WITHOUT
// one and the server returns the seed it minted; every later page sends that
// same seed back, so the ordering — and therefore the paging — cannot shift
// underneath the reader. Omitting it again (a reload) reshuffles the feed.
//
// `lang` / `category` are optional explicit filters for this request only
// (lang: a language name, or "all"); omitted, the server applies its default
// rules (saved languages while searching, none while browsing).
export async function getFeed(
  { query = "", interests = [], seed, page = 1, limit, lang, category } = {},
  signal
) {
  const params = {};
  if (query) params.q = query;
  if (lang) params.lang = lang;
  if (category) params.category = category;
  if (interests.length) params.interests = interests.join(",");
  if (seed) params.seed = seed;
  if (page > 1) params.page = page;
  if (limit) params.limit = limit;

  const res = await axios.get(`${BASE_URL}/api/feed`, {
    ...authConfig(),
    params,
    signal,
  });
  return res.data;
}
